import React from 'react'
import { Button, Input, Label, Modal, Textarea } from '@/shared/ui'
import { useToast } from '@/shared/ui/Toast'
import type { FinanceCostCategory, FinanceExpense } from '../../services/financeCostsService'
import { upsertFinanceExpense } from '../../services/financeCostsService'
import { centsToBrlInput, parseBrlToCents } from '../../utils/money'
import { monthInputToMonthStartDate, monthStartDateToMonthInput } from '../../utils/month'

type Props = {
  open: boolean
  onClose: () => void
  onSaved: () => void
  categories: FinanceCostCategory[]
  initial?: FinanceExpense | null
  defaultMonthStartDate: string
}

function dateToIsoStart(date: string): string | null {
  if (!date) return null
  try { return new Date(`${date}T00:00:00`).toISOString() } catch { return null }
}

function isoToDate(iso?: string | null): string {
  if (!iso) return ''
  try { return new Date(iso).toISOString().slice(0, 10) } catch { return '' }
}

export function FinanceExpenseModal({ open, onClose, onSaved, categories, initial, defaultMonthStartDate }: Props) {
  const { add: toast } = useToast()
  const [categoryId, setCategoryId] = React.useState<string>(initial?.category_id || '')
  const [name, setName] = React.useState(initial?.name || '')
  const [amountBrl, setAmountBrl] = React.useState<string>(() => centsToBrlInput(initial?.amount_cents || 0))
  const [competenceMonth, setCompetenceMonth] = React.useState<string>(() => {
    const base = initial?.competence_month || defaultMonthStartDate
    return base ? monthStartDateToMonthInput(base) : ''
  })
  const [paidDate, setPaidDate] = React.useState<string>(() => isoToDate(initial?.paid_at || null))
  const [notes, setNotes] = React.useState<string>(initial?.notes || '')
  const [saving, setSaving] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (!open) return
    setCategoryId(initial?.category_id || '')
    setName(initial?.name || '')
    setAmountBrl(centsToBrlInput(initial?.amount_cents || 0))
    const base = initial?.competence_month || defaultMonthStartDate
    setCompetenceMonth(base ? monthStartDateToMonthInput(base) : '')
    setPaidDate(isoToDate(initial?.paid_at || null))
    setNotes(initial?.notes || '')
    setSaving(false)
    setError(null)
  }, [open, initial, defaultMonthStartDate])

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    try {
      setSaving(true)
      const competence_month = monthInputToMonthStartDate(competenceMonth)
      const amount_cents = parseBrlToCents(amountBrl)
      await upsertFinanceExpense({
        id: initial?.id,
        category_id: categoryId ? categoryId : null,
        name,
        amount_cents,
        currency: 'BRL',
        competence_month,
        paid_at: dateToIsoStart(paidDate),
        notes: notes.trim() ? notes.trim() : null,
      })
      toast({ title: 'Despesa salva', description: name.trim(), variant: 'success', soft: true })
      onSaved()
      onClose()
    } catch (e: any) {
      setError(e?.message || 'Falha ao salvar despesa')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal open={open} onOpenChange={(v) => { if (!v) onClose() }}>
      <div className="mb-4">
        <h2 className="text-lg font-medium">{initial?.id ? 'Editar despesa' : 'Nova despesa'}</h2>
      </div>
      <form onSubmit={onSubmit} className="grid gap-4 sm:grid-cols-2">
        {error && <div className="sm:col-span-2 text-sm text-red-400">{error}</div>}

        <div className="space-y-1.5 sm:col-span-2">
          <Label>Nome</Label>
          <Input value={name} onChange={(e) => setName((e.target as HTMLInputElement).value)} placeholder="Ex.: Google Ads" />
        </div>

        <div className="space-y-1.5">
          <Label>Categoria</Label>
          <select
            className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
          >
            <option value="">(Sem categoria)</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>

        <div className="space-y-1.5">
          <Label>Valor (R$)</Label>
          <Input
            type="number"
            min="0"
            step="0.01"
            value={amountBrl}
            onChange={(e) => setAmountBrl((e.target as HTMLInputElement).value)}
            placeholder="0,00"
          />
        </div>

        <div className="space-y-1.5">
          <Label>Competência (mês)</Label>
          <Input type="month" value={competenceMonth} onChange={(e) => setCompetenceMonth((e.target as HTMLInputElement).value)} required />
        </div>

        <div className="space-y-1.5">
          <Label>Pago em (opcional)</Label>
          <Input type="date" value={paidDate} onChange={(e) => setPaidDate((e.target as HTMLInputElement).value)} />
        </div>

        <div className="space-y-1.5 sm:col-span-2">
          <Label>Observações</Label>
          <Textarea value={notes} onChange={(e) => setNotes((e.target as HTMLTextAreaElement).value)} placeholder="Opcional" />
        </div>

        <div className="sm:col-span-2 flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
          <Button type="submit" disabled={saving}>{saving ? 'Salvando...' : 'Salvar'}</Button>
        </div>
      </form>
    </Modal>
  )
}
